import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import List from 'components/Common/List'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { sortBy } from 'lib/array/arrayUtils'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import getPropertyIcon from 'lib/offer/getPropertyIcon'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'

const AmenitiesList = styled(VerticalSpacer)`
  margin-top: 0;

  ${mediaQueryUp.tablet} {
    columns: 2;
  }
`

interface RoomPackageAmenityItem {
  key: string
  description: string
  iconName?: App.Property.IconName
  offerSymbol?: App.OfferFeatureSymbol
}

export interface RoomPackageAmenityGroup {
  key: string
  title?: string
  items: Array<RoomPackageAmenityItem>
}

export function extractPropertyRoomPackageAmenityGroups({ amenities }: App.Property.Room): Array<RoomPackageAmenityGroup> {
  return amenities.map<RoomPackageAmenityGroup>(({ name, items }) => ({
    key: name,
    title: name,
    items: items.map<RoomPackageAmenityItem>(({ id, name, iconName }) => ({
      key: id,
      description: name,
      iconName,
    })),
  }))
}

export function extractHotelRoomPackageAmenityGroups(roomPackage: App.HotelPackage): Array<RoomPackageAmenityGroup> {
  if (!roomPackage.roomType.amenities.length) return []

  const sortedAmenities = sortBy(
    roomPackage.roomType.amenities.map<RoomPackageAmenityItem>((a) => {
      return { key: `${a.group}-${a.id}`, description: a.description, offerSymbol: a.symbol }
    }), a => a.description,
    'asc',
  )

  return [{
    key: `${roomPackage.id}-amenities`,
    items: sortedAmenities,
  }]
}

export function extractBedbankRoomPackageAmenityGroups(roomPackage: App.BedbankPackage): Array<RoomPackageAmenityGroup> {
  return roomPackage.facilityGroups.map<RoomPackageAmenityGroup>((facilityGroup) => {
    return {
      key: facilityGroup.name,
      title: facilityGroup.name,
      items: facilityGroup.values.map<RoomPackageAmenityItem>((v) => ({
        key: v.name,
        description: v.name,
      })),
    }
  })
}

function resolveAmenityIcon(item: RoomPackageAmenityItem) {
  if (item.iconName) return getPropertyIcon(item.iconName)
  if (item.offerSymbol) return getOfferFeatureIcon(item.offerSymbol)
  return undefined
}

interface Props {
  amenities: Array<RoomPackageAmenityGroup>
}

function AccommodationAmenitiesList({ amenities }: Props) {
  return <AmenitiesList gap={8}>
    {amenities.map(amenity => {
      const iconItems = amenity.items.filter((item) => resolveAmenityIcon(item))
      const plainItems = amenity.items.filter((item) => !resolveAmenityIcon(item))

      return <div key={amenity.key}>
        {amenity.title && <BodyText variant="medium" weight="bold">{amenity.title}</BodyText>}
        {iconItems.map((item) => {
          const Icon = resolveAmenityIcon(item)!
          return <BodyTextBlock key={item.key} variant="medium" startIcon={<Icon aria-hidden="true" />}>
            {item.description}
          </BodyTextBlock>
        })}
        {plainItems.length > 0 && <List>
          {plainItems.map((item) => <BodyText key={item.key} as="li" variant="medium">
            {item.description}
          </BodyText>)}
        </List>}
      </div>
    })}
  </AmenitiesList>
}

export default AccommodationAmenitiesList
